/**
 * Two-step backup email (PB-81). The server functions do the work
 * (`functions/src/functions/twoStepBackupEmail.ts`, `twoStepRecovery.ts`);
 * this only calls them and reads the user's own `twoStepRecovery` record.
 */
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from '@/firebase'
import { functionUrl } from '@/services/functionsUrl'
import { logger } from '@/services/logger'

export interface TwoStepRecoveryRecord {
  backupEmail?: string
  setupRequired?: boolean
}

/** The signed-in user's backup email and "set up again" flag. */
export async function getTwoStepRecovery(): Promise<TwoStepRecoveryRecord> {
  const uid = auth.currentUser?.uid
  if (!uid) return {}
  const snap = await getDoc(doc(db, 'twoStepRecovery', uid)).catch(err => {
    logger.warn('[twoStepBackup] read failed', err)
    return null
  })
  return (snap?.data() as TwoStepRecoveryRecord | undefined) ?? {}
}

/** A failed call; `code` matches Firebase's for a stale sign-in so the
 *  Security tab's confirm-it's-you dialog can take over. */
export class TwoStepBackupError extends Error {
  constructor(public code: string) {
    super(code)
  }
}

async function call(
  name: 'twoStepBackupEmail' | 'twoStepRecovery',
  body: Record<string, string>,
  signedIn: boolean
): Promise<{ status?: string; email?: string }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (signedIn) {
    // Fresh token: after "confirm it's you" its auth_time is new
    const token = await auth.currentUser?.getIdToken(true)
    if (!token) throw new TwoStepBackupError('not-signed-in')
    headers.Authorization = `Bearer ${token}`
  }
  const res = await fetch(functionUrl(name), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as {
    status?: string
    email?: string
    error?: string
  }
  if (!res.ok) {
    const code =
      json.error === 'requires-recent-login'
        ? 'auth/requires-recent-login'
        : json.error || (res.status === 429 ? 'too-many' : 'failed')
    logger.warn(`[twoStepBackup] ${name} refused`, { status: res.status, code })
    throw new TwoStepBackupError(code)
  }
  return json
}

export const startBackupEmail = (email: string) =>
  call('twoStepBackupEmail', { action: 'start', email }, true)
export const confirmBackupEmail = (code: string) =>
  call('twoStepBackupEmail', { action: 'confirm', code }, true)
export const removeBackupEmail = () =>
  call('twoStepBackupEmail', { action: 'remove' }, true)

/** Who is signing in: the account id from the second-step error when
 *  Firebase gives it (a Google email can differ from the account's), else
 *  the typed email. */
export interface RecoveryAccount {
  uid?: string
  email?: string
}
const who = (a: RecoveryAccount) => ({
  ...(a.uid && { uid: a.uid }),
  ...(a.email && { email: a.email }),
})

/** Sign-in code screen: `sent`, or `none` when there's no backup email. */
export const sendRecoveryCode = (account: RecoveryAccount) =>
  call('twoStepRecovery', { action: 'send', ...who(account) }, false)
export const verifyRecoveryCode = (account: RecoveryAccount, code: string) =>
  call('twoStepRecovery', { action: 'verify', ...who(account), code }, false)
